/* eslint-disable react/display-name */
/* eslint-disable no-console */
import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { api } from '../../../config';

const DmutInvitationDataTable = forwardRef(
  (
    {
      procurementId,
      token,
      subStatusTitles = [],
      cargoCategories = [],
      onSelectionChange,
      selectedMitraList = [],
      procurementType,
      isProcurementCompleted,
    },
    ref
  ) => {
    const [data, setData] = useState({ items: [] });
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const initialSyncDone = useRef(false);
    const selectAllRef = useRef(null);

    const syncDataDmut = useCallback(async () => {
      const url = `${api.API_URL}/dmut/SynchronizationDataDmut`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Sync DMUT Data failed: ${response.status}`);
      }
      return response.json();
    }, [token]);

    const syncCompanyInfo = useCallback(async () => {
      const url = `${api.API_URL}/dmut/SynchronizeCompanyInfoData`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Sync Company Info failed: ${response.status}`);
      }
      return response.json();
    }, [token]);

    useImperativeHandle(ref, () => ({
      refresh: async () => {
        setLoading(true);
        try {
          await syncDataDmut();
          await syncCompanyInfo();
          await fetchData(currentPage, rowsPerPage);
        } catch (error) {
          console.error('Synch DMUT error:', error);
          toast.error(error.message || 'Failed to synchronize DMUT data');
        } finally {
          setLoading(false);
        }
      },
    }));

    useEffect(() => {
      if (selectedMitraList.length > 0 && !initialSyncDone.current) {
        const ids = selectedMitraList.map(item => item.companyMitraId);
        setSelectedIds(ids);
        initialSyncDone.current = true;
      }
    }, [selectedMitraList]);

    useEffect(() => {
      if (procurementType !== 'PelelanganKhusus' || data.items.length === 0) {
        return;
      }
      const allIds = data.items.map(row => row.id);
      const alreadySelected =
        allIds.length === selectedIds.length &&
        allIds.every(id => selectedIds.includes(id));
      if (alreadySelected) {
        return;
      }
      setSelectedIds(allIds);
      onSelectionChange?.(allIds.map(id => ({ id: null, companyMitraId: id })));
      // onSelectionChange intentionally excluded: parent often passes a new
      // function reference each render, and including it here would re-fire
      // this effect after every call, causing an infinite update loop.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [procurementType, data.items, selectedIds]);

    const handleToggleDmut = (id, checked) => {
      if (procurementType === 'PelelanganKhusus') {
        return;
      }
      if (checked) {
        if (procurementType === 'TunjukLangsung' && selectedIds.length >= 1) {
          toast.warning('Tunjuk Langsung hanya dapat memilih 1 DMUT');
          return;
        }
      } else {
        if (procurementType !== 'TunjukLangsung' && selectedIds.length <= 2) {
          toast.warning('Minimal 2 DMUT harus dipilih');
          return;
        }
      }
      setSelectedIds(prev => {
        const next = checked
          ? [...prev, id]
          : prev.filter(itemId => itemId !== id);
        onSelectionChange?.(
          next.map(itemId => ({ id: null, companyMitraId: itemId }))
        );
        return next;
      });
    };

    const handleSelectAll = e => {
      if (procurementType === 'PelelanganKhusus') {
        return;
      }
      if (procurementType === 'TunjukLangsung') {
        toast.warning('Tunjuk Langsung hanya dapat memilih 1 DMUT');
        return;
      }
      if (e.target.checked && data.items.length < 2) {
        toast.warning('Minimal 2 DMUT tersedia untuk dipilih');
        return;
      }
      const checked = e.target.checked;
      if (checked) {
        const allIds = data.items.map(row => row.id);
        setSelectedIds(allIds);
        onSelectionChange?.(
          allIds.map(id => ({ id: null, companyMitraId: id }))
        );
      } else {
        setSelectedIds([]);
        onSelectionChange?.([]);
      }
    };

    useEffect(() => {
      if (selectAllRef.current) {
        const isIndeterminate =
          selectedIds.length > 0 && selectedIds.length < data.items.length;
        selectAllRef.current.indeterminate = isIndeterminate;
      }
    }, [selectedIds, data.items]);

    const columns = [
      {
        name: (
          <input
            className='form-check-input'
            type='checkbox'
            checked={
              data.items.length > 0 && selectedIds.length === data.items.length
            }
            ref={selectAllRef}
            onChange={handleSelectAll}
            disabled={
              isProcurementCompleted !== false ||
              procurementType === 'PelelanganKhusus'
            }
          />
        ),
        selector: row => selectedIds.includes(row.id),
        width: '10%',
        center: true,
        cell: row => (
          <input
            className='form-check-input'
            type='checkbox'
            checked={selectedIds.includes(row.id)}
            onChange={e => handleToggleDmut(row.id, e.target.checked)}
            disabled={
              isProcurementCompleted !== false ||
              procurementType === 'PelelanganKhusus'
            }
          />
        ),
      },
      {
        name: (
          <span className=' text-uppercase fw-semibold fs-13'>DMUT Name</span>
        ),
        selector: row => row.companyName,
        sortable: true,
        width: '40%',
        wrap: true,
        cell: row => <h6 className='mb-0'>{row.companyName || '-'}</h6>,
      },
      {
        name: (
          <span className=' text-uppercase fw-semibold fs-13'>PIC Name</span>
        ),
        selector: row => row.pic,
        sortable: true,
        width: '20%',
        wrap: true,
        cell: row => <h6 className='mb-0'>{row.pic || '-'}</h6>,
      },
      {
        name: (
          <span className=' text-uppercase fw-semibold fs-13'>PIC Email</span>
        ),
        selector: row => row.settlementEmail,
        sortable: true,
        width: '30%',
        wrap: true,
        cell: row => <h6 className='mb-0'>{row.settlementEmail || '-'}</h6>,
      },
    ];

    const fetchData = useCallback(
      async (page, size) => {
        if (!procurementId) return;
        setLoading(true);
        try {
          let url = `${api.API_URL}/procurement/get-dmut-for-invitation?ProcurementId=${procurementId}&PageNumber=${page}&PageSize=${size}`;

          if (subStatusTitles.length > 0) {
            url += subStatusTitles
              .map(title => `&SubStatus=${encodeURIComponent(title)}`)
              .join('');
          }
          if (cargoCategories.length > 0) {
            url += cargoCategories
              .map(cat => `&CargoCategory=${encodeURIComponent(cat)}`)
              .join('');
          }

          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            credentials: 'same-origin',
          });
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const result = await response.json();
          setData({
            items: result.data?.items || result.items || [],
          });
          setTotalRows(result.data?.totalItems || result.totalItems || 0);
        } catch (error) {
          console.error('Error fetching DMUT invitation data:', error);
        } finally {
          setLoading(false);
        }
      },
      [procurementId, token, subStatusTitles, cargoCategories]
    );

    useEffect(() => {
      setCurrentPage(1);
      fetchData(1, rowsPerPage);
    }, [fetchData, rowsPerPage]);

    const handlePageChange = useCallback(
      page => {
        setCurrentPage(page);
        fetchData(page, rowsPerPage);
      },
      [fetchData, rowsPerPage]
    );

    const handlePerRowsChange = useCallback(newPerPage => {
      setRowsPerPage(newPerPage);
      setCurrentPage(1);
    }, []);

    return (
      <DataTable
        columns={columns}
        data={data.items}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        highlightOnHover
        responsive
        striped
        persistTableHead
        progressPending={loading}
        progressComponent={
          <div className='text-center py-4'>
            <div className='spinner-border text-primary' role='status'>
              <span className='sr-only'>Loading...</span>
            </div>
            <p className='mt-2 text-muted'>Loading data...</p>
          </div>
        }
        paginationRowsPerPageOptions={[10, 15, 25, 50]}
        noDataComponent={
          <div className='text-center py-4'>
            <div className='mb-3'>
              <i className='ri-search-line fs-2 text-muted'></i>
            </div>
            <h5 className='mt-2'>No DMUT data available</h5>
            <p className='text-muted mb-0'>No DMUT invitation data found.</p>
          </div>
        }
        customStyles={{
          table: {
            style: {
              tableLayout: 'auto',
              width: '100%',
            },
          },
          header: {
            style: {
              backgroundColor: '#f8f9fa',
              color: '#495057',
              fontWeight: 'bold',
              borderBottom: '1px solid #dee2e6',
            },
          },
          headCells: {
            style: {
              backgroundColor: '#f8f9fa',
              fontWeight: '600',
              fontSize: '13px',
              color: '#495057',
              paddingLeft: '12px',
              paddingRight: '12px',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            },
          },
          rows: {
            style: {
              minHeight: '30px',
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#f8f9fa',
              },
            },
          },
          cells: {
            style: {
              paddingLeft: '12px',
              paddingRight: '12px',
              wordBreak: 'break-word',
            },
          },
          pagination: {
            style: {
              borderTop: '1px solid #dee2e6',
            },
          },
        }}
      />
    );
  }
);

export default DmutInvitationDataTable;
